import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Capacitor } from '@capacitor/core';
import { SafeArea } from 'capacitor-plugin-safe-area';
import App from "./App.tsx";
import "./index.css";

// Dopo un deploy i chunk JS cambiano hash: un client con l'index vecchio (es. WebView
// Despia in cache) fallisce l'import dinamico. Vite emette 'vite:preloadError'.
//
// Ricaricare subito però non basta: durante un deploy c'è una finestra in cui l'index
// nuovo è già servito ma qualche chunk no, e la richiesta cade nel fallback SPA che
// risponde 200 con HTML al posto del JavaScript. Una ricarica immediata ricasca nella
// stessa finestra e si arrende, mostrando l'errore. Quindi: due tentativi, il secondo
// più lontano, così la finestra passa.
//
// Il contatore NON va azzerato all'avvio dell'app: qui l'app parte benissimo, a
// fallire è un chunk di rotta caricato subito dopo. Azzerandolo a ogni avvio il
// tetto dei tentativi non veniva mai raggiunto e la pagina si ricaricava all'infinito.
// Il conto è legato alla versione del bundle: un deploy nuovo lo azzera da sé.
const ATTESE_MS = [1200, 4000];
const CHIAVE_CHUNK = "cl-chunk-reloaded";

function tentativiChunk(): number {
  try {
    const salvato = sessionStorage.getItem(CHIAVE_CHUNK);
    if (!salvato) return 0;
    const { build, n } = JSON.parse(salvato) as { build?: string; n?: number };
    return build === __BUILD_ID__ ? Number(n) || 0 : 0;
  } catch {
    return 0;
  }
}

/**
 * Ripara la cache del browser quando contiene HTML al posto di un modulo.
 *
 * Durante la finestra di propagazione di un deploy un chunk non ancora online cade
 * nel fallback SPA, che risponde 200 con l'HTML dell'app. Il browser lo archivia
 * sotto l'URL del chunk e da quel momento la pagina che lo importa non si apre più,
 * nemmeno ricaricando: l'URL non cambia finché quel file non viene modificato.
 *
 * Una fetch con cache "reload" va in rete e sovrascrive la voce marcia (verificato
 * in produzione su una cache realmente avvelenata). Qui si percorre il grafo dei
 * moduli a partire da quello fallito e si ripara ciò che serve.
 */
const MAX_MODULI = 300;

async function riparaCacheModuli(partenza: string): Promise<number> {
  const visti = new Set<string>();
  const coda = [partenza];
  let riparati = 0;

  while (coda.length && visti.size < MAX_MODULI) {
    const url = coda.shift()!;
    if (visti.has(url)) continue;
    visti.add(url);

    let testo: string;
    try {
      testo = await fetch(url, { cache: "force-cache" }).then((r) => r.text());
    } catch {
      continue;
    }

    // Un modulo che inizia con "<" è l'HTML del fallback finito al posto del codice.
    if (testo.trimStart().startsWith("<")) {
      try {
        testo = await fetch(url, { cache: "reload" }).then((r) => r.text());
        riparati++;
      } catch {
        continue;
      }
    }

    for (const m of testo.matchAll(/"\.\/([^"]+\.js)"/g)) {
      try {
        coda.push(new URL(m[1], url).href);
      } catch {
        // specificatore non risolvibile: si ignora
      }
    }
  }
  return riparati;
}

window.addEventListener("vite:preloadError", (e) => {
  const fatti = tentativiChunk();
  if (fatti >= ATTESE_MS.length) return;
  sessionStorage.setItem(CHIAVE_CHUNK, JSON.stringify({ build: __BUILD_ID__, n: fatti + 1 }));

  // L'URL del modulo che non si è caricato sta nel messaggio dell'errore.
  const messaggio = String((e as Event & { payload?: Error }).payload?.message ?? "");
  const fallito = messaggio.match(/https?:\/\/\S+?\.js/)?.[0];

  const ricarica = () => {
    // Reload cache-busting: cambia l'URL così il WebView riscarica l'index fresco
    // invece di riusare quello in cache che punta ai chunk spariti.
    const u = new URL(window.location.href);
    u.searchParams.set("_r", Date.now().toString());
    window.location.replace(u.toString());
  };

  setTimeout(() => {
    // Prima di ricaricare si ripara la cache: se il problema è HTML archiviato al
    // posto di un modulo, ricaricare da solo non basta — l'URL resta lo stesso.
    if (fallito && new URL(fallito).origin === window.location.origin) {
      riparaCacheModuli(fallito).then(ricarica).catch(ricarica);
    } else {
      ricarica();
    }
  }, ATTESE_MS[fatti]);
});

// Initialize iOS safe area insets for toast and UI positioning
if (Capacitor.isNativePlatform()) {
  SafeArea.getSafeAreaInsets().then(({ insets }) => {
    const root = document.documentElement;
    root.style.setProperty('--safe-area-top', `${insets.top}px`);
    root.style.setProperty('--safe-area-right', `${insets.right}px`);
    root.style.setProperty('--safe-area-bottom', `${insets.bottom}px`);
    root.style.setProperty('--safe-area-left', `${insets.left}px`);
  }).catch(console.error);
}

// Sigla del build: non più a schermo, ma resta recuperabile da console e da
// window.__CL_BUILD__ quando serve capire quale bundle sta girando su un dispositivo.
(window as unknown as { __CL_BUILD__: string }).__CL_BUILD__ = __BUILD_ID__;
console.info("build", __BUILD_ID__);

// L'app è partita: si azzera solo il contatore dell'AVVIO, che riguarda il caso in
// cui l'app non parte affatto — se siamo qui, quel caso è escluso.
// Il contatore dei chunk resta: l'app parte comunque anche quando manca un chunk di
// rotta, e azzerarlo qui creava un ciclo infinito di ricariche.
sessionStorage.removeItem("cl-boot-riprovato");

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
